import { useCallback } from "react";
import { message } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";

import { useAddProfilePhotoMutation } from "graphql/mutations/add-profile-photo.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { uploadToStorage } from "utils/files-management";

/**
 * Returns a function that uploads a new profile photo of the logged user
 * and saves its URL in the user record and in the local user data.
 */
export const useProfilePhotoUpload = () => {
	const { loggedUserValue, updateLoggedUser } = useLoggedUser();

	const [addProfilePhotoMutation] = useAddProfilePhotoMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: () => {
			message.error(`Profile photo upload failed.`);
		},
		onCompleted: (data: any) => {
			const { profile_photo } = data.update_users_by_pk;
			updateLoggedUser({ profile_photo });
		}
	});

	return useCallback(
		async (file: RcFile) => {
			try {
				const uniqueID = uuidv4();
				const bucketName = "profile-photos/";
				const fileName = `profile-photo-${loggedUserValue.id}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${file.name}`;

				const { publicUrl } = await uploadToStorage(file, () => {}, `${bucketName}/${fileName}`);

				await addProfilePhotoMutation({
					variables: {
						userId: loggedUserValue.id,
						objects: { profile_photo: publicUrl }
					}
				});
			} catch (error) {
				console.error("Profile photo upload failed:", error);
				throw error;
			}
		},
		[loggedUserValue.id, addProfilePhotoMutation]
	);
};
